import { useState, useEffect, useRef } from 'react';

declare global {
  interface Window {
    Chart: any;
  }
}

export default function Services() {
  const [activeTab, setActiveTab] = useState<'house' | 'grave'>('house');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<string>('');
  const [gardenDetailModalOpen, setGardenDetailModalOpen] = useState(false);
  const [weedingDetailModalOpen, setWeedingDetailModalOpen] = useState(false);
  const [annualDetailModalOpen, setAnnualDetailModalOpen] = useState(false);
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<any>(null);

  useEffect(() => {
    if (!annualDetailModalOpen) return;

    const initChart = () => {
      if (!chartRef.current || !window.Chart) return;
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
      chartInstanceRef.current = new window.Chart(chartRef.current, {
        type: 'bar',
        data: {
          labels: ['비구독 (연간)', '스탠다드 구독', '프리미엄 구독'],
          datasets: [
            {
              label: '예상 비용 (만원)',
              data: [380, 120, 240],
              backgroundColor: ['#e2e8f0', '#4ade80', '#166534'],
              borderRadius: 8,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            y: {
              beginAtZero: true,
              ticks: { callback: (v: number) => v + '만' },
            },
          },
        },
      });
    };

    if (window.Chart) {
      setTimeout(initChart, 100);
    } else {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/chart.js';
      script.onload = () => setTimeout(initChart, 100);
      document.head.appendChild(script);
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [annualDetailModalOpen]);

  const handleQuoteClick = (serviceName: string) => {
    setSelectedService(serviceName);
    setModalOpen(true);
  };

  const handleGardenDetailClick = () => setGardenDetailModalOpen(true);
  const handleWeedingDetailClick = () => setWeedingDetailModalOpen(true);
  const handleAnnualDetailClick = () => setAnnualDetailModalOpen(true);

  const houseServices = [
    {
      name: '정원/조경 관리',
      price: '별도 견적',
      description: '면적 및 수종별 맞춤 관리',
      image: 'https://public.readdy.ai/ai/img_res/cc83d0c43193a28c0394e79df49afc3e.jpg',
      isGardenService: true,
    },
    {
      name: '제초 및 정리',
      price: '80,000원~',
      description: '21개 요인 기반 투명 견적',
      image: 'https://readdy.ai/api/search-image?query=professional%20grass%20cutting%20and%20land%20clearing%20service%20around%20Korean%20countryside%20house%2C%20worker%20using%20modern%20grass%20cutting%20equipment%20on%20large%20property%2C%20clean%20organized%20work%2C%20simple%20background%20highlighting%20the%20maintained%20area%2C%20bright%20daylight%2C%20professional%20service%20documentation%20photography&width=400&height=300&seq=service-clearing-001&orientation=landscape',
      isWeedingService: true,
    },
    {
      name: '계절별 유지보수',
      price: '연간 구독',
      description: '사계절 통합 관리 패키지',
      image: 'https://readdy.ai/api/search-image?query=four%20seasons%20maintenance%20service%20for%20Korean%20countryside%20house%2C%20professional%20team%20performing%20seasonal%20maintenance%20work%20including%20winter%20snow%20removal%20and%20summer%20garden%20care%2C%20systematic%20year-round%20property%20management%2C%20clean%20simple%20background%2C%20natural%20lighting%2C%20premium%20service%20photography&width=400&height=300&seq=service-seasonal-001&orientation=landscape',
      isAnnualService: true,
    },
  ];

  const graveServices = [
    {
      name: '벌초 대행 (기본)',
      price: '100,000원~',
      description: '21개 요인 기반 견적',
      image: 'https://static.readdy.ai/image/b6088df85b1af9036dd560688f3d7991/67fdb5bc864092678011c2f95027d17c.jpeg',
    },
    {
      name: '묶음 벌초 할인',
      price: '300,000원',
      description: '4기 기준 동일 구역',
      image: 'https://static.readdy.ai/image/b6088df85b1af9036dd560688f3d7991/7a7017cb32986ecaf465e7169b538a52.png',
    },
    {
      name: '전문 석축/보수',
      price: '230,000원/평',
      description: '고난도 전문 공사',
      image: 'https://static.readdy.ai/image/b6088df85b1af9036dd560688f3d7991/5fa6e445ec8eb061f8ebdb65dccafb6b.png',
    },
  ];

  const currentServices = activeTab === 'house' ? houseServices : graveServices;

  const getButtonLabel = (service: any) => {
    if (service.isGardenService || service.isWeedingService || service.isAnnualService) return '서비스 내용보기';
    return '견적 문의하기';
  };

  const handleServiceClick = (service: any) => {
    if (service.isGardenService) handleGardenDetailClick();
    else if (service.isWeedingService) handleWeedingDetailClick();
    else if (service.isAnnualService) handleAnnualDetailClick();
    else handleQuoteClick(service.name);
  };

  return (
    <section id="services" className="relative py-16 sm:py-28 px-4 sm:px-6 overflow-hidden" style={{background: 'linear-gradient(155deg, #0d1f3c 0%, #152a52 50%, #111e3a 100%)'}}>
      <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'linear-gradient(125deg, transparent 43%, #c9a227 44%, #c9a227 44.4%, transparent 45%), linear-gradient(85deg, transparent 57%, #c9a227 58%, #c9a227 58.3%, transparent 59%)', backgroundSize: '380px 380px'}}></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Title */}
        <div className="mb-10 sm:mb-14 text-center">
          <h2 className="text-2xl sm:text-5xl font-black text-white mb-3 sm:mb-4 leading-[1.2] tracking-tight" style={{ fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 900 }}>
            투명한 가격, 명확한 가치
          </h2>
          <p className="text-sm sm:text-lg text-[#c9a227]/80 font-medium leading-relaxed">
            정찰제 기반 핵심 서비스
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-10 sm:mb-14">
          <div className="inline-flex rounded-full p-1 shadow-sm border border-[#c9a227]/20" style={{background: 'rgba(13,31,60,0.8)'}}>
            <button
              onClick={() => setActiveTab('house')}
              className={`px-5 sm:px-8 py-2.5 sm:py-3 rounded-full text-sm sm:text-base font-medium transition-all whitespace-nowrap cursor-pointer ${activeTab === 'house' ? 'text-[#0d1f3c] font-bold' : 'text-white/70 hover:text-white'}`}
              style={activeTab === 'house' ? {background: 'linear-gradient(135deg, #e8c84a, #b8860b)'} : {}}
            >
              전원주택 관리
            </button>
            <button
              onClick={() => setActiveTab('grave')}
              className={`px-5 sm:px-8 py-2.5 sm:py-3 rounded-full text-sm sm:text-base font-medium transition-all whitespace-nowrap cursor-pointer ${activeTab === 'grave' ? 'text-[#0d1f3c] font-bold' : 'text-white/70 hover:text-white'}`}
              style={activeTab === 'grave' ? {background: 'linear-gradient(135deg, #e8c84a, #b8860b)'} : {}}
            >
              산소·선산 관리
            </button>
          </div>
        </div>

        {/* Service Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-8">
          {currentServices.map((service, index) => (
            <div key={index} className="rounded-2xl sm:rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow border border-[#c9a227]/20" style={{background: 'linear-gradient(160deg, rgba(30,58,110,0.85), rgba(13,31,60,0.95))'}}>
              <div className="rounded-[20px] sm:rounded-[28px] m-3 sm:m-4 overflow-hidden border border-[#c9a227]/15" style={{ height: '180px' }}>
                <img src={service.image} alt={service.name} className="w-full h-full object-cover object-top" />
              </div>
              <div className="px-6 sm:px-7 pb-7 sm:pb-8 pt-1 text-center">
                <h3 className="text-xl sm:text-2xl font-medium text-white mb-2.5 sm:mb-3 tracking-tight">{service.name}</h3>
                <p className="text-3xl sm:text-4xl font-bold text-[#c9a227] mb-5 sm:mb-7 tracking-tight">{service.price}</p>
                <button
                  onClick={() => handleServiceClick(service)}
                  className="w-4/5 mx-auto py-3 sm:py-3.5 rounded-full text-sm sm:text-base font-bold hover:scale-105 transition-transform cursor-pointer whitespace-nowrap text-[#0d1f3c]"
                  style={{background: 'linear-gradient(135deg, #e8c84a, #b8860b)'}}
                >
                  {getButtonLabel(service)}
                </button>
                <p className="text-xs text-white/40 mt-3.5 sm:mt-4 leading-relaxed">{service.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Quote Modal ── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }} onClick={() => setModalOpen(false)}>
          <div className="rounded-3xl shadow-2xl max-w-md w-full p-8 sm:p-10 relative border border-[#c9a227]/25" style={{background: 'linear-gradient(160deg, #1e3a6e, #0d1f3c)'}} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setModalOpen(false)} className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors cursor-pointer">
              <i className="ri-close-line text-white text-lg"></i>
            </button>
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl mb-5 mx-auto border border-[#c9a227]/30" style={{background: 'rgba(201,162,39,0.15)'}}>
              <i className="ri-file-list-3-line text-[#c9a227] text-2xl"></i>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-white text-center mb-2 tracking-tight">견적 문의 안내</h3>
            <p className="text-sm text-[#c9a227] font-semibold text-center mb-6">[{selectedService}]</p>
            <div className="rounded-2xl px-6 py-5 mb-6 space-y-3 border border-[#c9a227]/15" style={{background: 'rgba(201,162,39,0.08)'}}>
              <p className="text-sm sm:text-base text-white/85 leading-relaxed text-center">견적 문의를 주셔서 감사합니다! 😊</p>
              <p className="text-sm text-white/65 leading-relaxed text-center">아래 연락처로 문의 주시면<br /><strong className="text-white/90">빠르게 맞춤 견적</strong>을 안내해 드리겠습니다.</p>
            </div>
            <div className="space-y-3 mb-7">
              <a href="tel:010-9879-8375" className="flex items-center gap-3 rounded-2xl px-5 py-3.5 hover:opacity-90 transition-opacity cursor-pointer" style={{background: 'linear-gradient(135deg, #e8c84a, #b8860b)'}}>
                <div className="w-8 h-8 flex items-center justify-center"><i className="ri-phone-line text-[#0d1f3c] text-lg"></i></div>
                <div>
                  <p className="text-xs text-[#0d1f3c]/70 leading-none mb-0.5">전화 문의</p>
                  <p className="text-base font-bold tracking-wide text-[#0d1f3c]">010-9879-8375</p>
                </div>
              </a>
              <a href="https://open.kakao.com" target="_blank" rel="noopener noreferrer nofollow" className="flex items-center gap-3 bg-[#FEE500] text-[#3C1E1E] rounded-2xl px-5 py-3.5 hover:bg-[#f5dc00] transition-colors cursor-pointer">
                <div className="w-8 h-8 flex items-center justify-center"><i className="ri-kakao-talk-fill text-lg"></i></div>
                <div>
                  <p className="text-xs opacity-60 leading-none mb-0.5">카카오톡 문의</p>
                  <p className="text-base font-bold">카카오 채널 바로가기</p>
                </div>
              </a>
            </div>
            <p className="text-xs text-white/40 text-center leading-relaxed">운영시간: 평일 09:00 ~ 18:00<br />문의 후 <strong className="text-white/60">24시간 이내</strong> 답변 드립니다.</p>
          </div>
        </div>
      )}

      {/* ── Garden Detail Modal ── */}
      {gardenDetailModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }} onClick={() => setGardenDetailModalOpen(false)}>
          <div className="relative max-w-4xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setGardenDetailModalOpen(false)} className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/90 hover:bg-white shadow-lg transition-colors cursor-pointer">
              <i className="ri-close-line text-gray-700 text-xl"></i>
            </button>
            <div className="bg-[#1B4332] p-10 text-white text-center relative">
              <div className="inline-block bg-green-500 text-xs font-bold px-3 py-1 rounded-full mb-4 uppercase tracking-widest text-white">Professional Gardener</div>
              <h1 className="text-2xl md:text-3xl font-bold mb-3">전문 가드너의 손길로 완성되는 <span className="text-green-300">명품 정원</span></h1>
              <p className="text-green-100 font-light text-sm md:text-base opacity-90">현장 노하우로 당신의 전원생활에 품격을 더해드립니다.</p>
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white text-[#1B4332] font-bold px-8 py-2.5 rounded-full shadow-lg border border-green-100 text-sm">고성 아름다운 정원의 진심</div>
            </div>
            <div className="p-8 md:p-14 pt-16">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="p-6 bg-green-50 rounded-2xl border border-green-100 transition-all hover:shadow-md">
                  <h3 className="text-lg font-bold text-[#1B4332] mb-4">01. 정기 조경 유지관리</h3>
                  <ul className="space-y-2 text-gray-600 text-sm">
                    <li className="flex items-start"><span className="text-green-500 mr-2">•</span><p><strong>정밀 예초:</strong> 잔디 건강을 고려한 최적 높이 조절</p></li>
                    <li className="flex items-start"><span className="text-green-500 mr-2">•</span><p><strong>수종별 전지:</strong> 수형과 성장을 돕는 전문 가지치기</p></li>
                    <li className="flex items-start"><span className="text-green-500 mr-2">•</span><p><strong>잡초 관리:</strong> 화단 및 보행로 집중 제거 작업</p></li>
                  </ul>
                </div>
                <div className="p-6 bg-blue-50 rounded-2xl border border-blue-100 transition-all hover:shadow-md">
                  <h3 className="text-lg font-bold text-blue-900 mb-4">02. 식생 건강 전문 케어</h3>
                  <ul className="space-y-2 text-gray-600 text-sm">
                    <li className="flex items-start"><span className="text-blue-500 mr-2">•</span><p><strong>병해충 방제:</strong> 선제적 예방 및 맞춤형 전문 소독</p></li>
                    <li className="flex items-start"><span className="text-blue-500 mr-2">•</span><p><strong>시비 작업:</strong> 적기 비료 투입으로 생육·색감 개선</p></li>
                    <li className="flex items-start"><span className="text-blue-500 mr-2">•</span><p><strong>수종 상담:</strong> 아름다운 정원 데이터 기반 수종 추천</p></li>
                  </ul>
                </div>
                <div className="p-6 bg-orange-50 rounded-2xl border border-orange-100 transition-all hover:shadow-md">
                  <h3 className="text-lg font-bold text-orange-900 mb-4">03. 시설물 유지보수</h3>
                  <ul className="space-y-2 text-gray-600 text-sm">
                    <li className="flex items-start"><span className="text-orange-500 mr-2">•</span><p><strong>석축/법면 점검:</strong> 유실 방지 상태 확인 및 정밀 보수</p></li>
                    <li className="flex items-start"><span className="text-orange-500 mr-2">•</span><p><strong>목재 케어:</strong> 데크 오일스테인 도색 및 파손 소수리</p></li>
                    <li className="flex items-start"><span className="text-orange-500 mr-2">•</span><p><strong>배수 정비:</strong> 장마철 대비 배수로 소통 및 낙엽 제거</p></li>
                  </ul>
                </div>
                <div className="p-6 bg-purple-50 rounded-2xl border border-purple-100 transition-all hover:shadow-md">
                  <h3 className="text-lg font-bold text-purple-900 mb-4">04. 프리미엄 안심 서비스</h3>
                  <ul className="space-y-2 text-gray-600 text-sm">
                    <li className="flex items-start"><span className="text-purple-500 mr-2">•</span><p><strong>포토 리포트:</strong> 작업 전/중/후 고화질 사진 전송</p></li>
                    <li className="flex items-start"><span className="text-purple-500 mr-2">•</span><p><strong>원격 점검:</strong> 기상 이변 시 긴급 방문 및 결과 보고</p></li>
                    <li className="flex items-start"><span className="text-purple-500 mr-2">•</span><p><strong>계절 알림:</strong> 적기 관리 시기 사전 안내 서비스</p></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Weeding Detail Modal ── */}
      {weedingDetailModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }} onClick={() => setWeedingDetailModalOpen(false)}>
          <div className="relative max-w-6xl w-full bg-stone-50 rounded-3xl shadow-2xl overflow-hidden border border-stone-200 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setWeedingDetailModalOpen(false)} className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/90 hover:bg-white shadow-lg transition-colors cursor-pointer">
              <i className="ri-close-line text-stone-700 text-xl"></i>
            </button>
            <div className="pt-20 pb-16 px-6 text-center bg-gradient-to-br from-stone-100 to-stone-50">
              <div className="max-w-4xl mx-auto">
                <h1 className="text-3xl md:text-5xl font-bold text-stone-900 mb-4 leading-tight">당신의 공간에 <span className="text-green-800">품격</span>을,<br className="hidden sm:block" />마음에 <span className="text-green-700">안심</span>을 드립니다.</h1>
                <p className="text-base text-stone-600 mb-8 max-w-2xl mx-auto leading-relaxed">단순한 벌초를 넘어, 베테랑의 시선으로 정성을 다합니다.<br />현장 노하우가 집약된 투명한 서비스 가이드를 지금 바로 확인하세요.</p>
              </div>
            </div>
            <div className="p-6 md:p-12">
              <div className="max-w-5xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-2xl md:text-3xl font-bold text-stone-800 mb-3">스마트 견적 대시보드</h2>
                  <p className="text-stone-500">면적과 작업 환경에 따른 표준 단가를 실시간으로 분석합니다.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                  <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-200">
                    <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-xl mb-4 mx-auto"><i className="ri-ruler-line text-green-700 text-2xl"></i></div>
                    <h3 className="text-lg font-bold text-stone-800 mb-2 text-center">면적 기반 견적</h3>
                    <p className="text-sm text-stone-600 text-center leading-relaxed">작업 면적에 따른<br />투명한 단가 적용</p>
                  </div>
                  <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-200">
                    <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-xl mb-4 mx-auto"><i className="ri-landscape-line text-blue-700 text-2xl"></i></div>
                    <h3 className="text-lg font-bold text-stone-800 mb-2 text-center">현장 환경 분석</h3>
                    <p className="text-sm text-stone-600 text-center leading-relaxed">경사도, 접근성 등<br />21개 요인 반영</p>
                  </div>
                  <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-200">
                    <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-xl mb-4 mx-auto"><i className="ri-shield-check-line text-orange-700 text-2xl"></i></div>
                    <h3 className="text-lg font-bold text-stone-800 mb-2 text-center">품질 보증</h3>
                    <p className="text-sm text-stone-600 text-center leading-relaxed">베테랑 전문가의<br />정성스러운 작업</p>
                  </div>
                </div>
                <div className="bg-white p-8 rounded-2xl shadow-sm border border-stone-200 mb-12">
                  <h3 className="text-xl font-bold text-stone-800 mb-6 text-center">표준 작업 공정</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {[{emoji:'📋',title:'1. 현장 분석',desc:'면적 및 환경 평가',bg:'bg-green-100'},{emoji:'🌿',title:'2. 제초 작업',desc:'전문 장비 활용',bg:'bg-blue-100'},{emoji:'🧹',title:'3. 정리 정돈',desc:'깔끔한 마무리',bg:'bg-orange-100'},{emoji:'📸',title:'4. 결과 보고',desc:'사진 리포트 제공',bg:'bg-purple-100'}].map((step, i) => (
                      <div key={i} className="text-center">
                        <div className={`w-14 h-14 flex items-center justify-center ${step.bg} rounded-full mb-3 mx-auto`}><span className="text-2xl">{step.emoji}</span></div>
                        <h4 className="font-bold text-stone-800 mb-1">{step.title}</h4>
                        <p className="text-xs text-stone-600">{step.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-gradient-to-br from-green-800 to-green-900 p-8 rounded-2xl text-center text-white">
                  <h3 className="text-xl md:text-2xl font-bold mb-3">지금 바로 무료 상담 받으세요</h3>
                  <p className="text-green-100 mb-6 text-sm">전문가가 현장을 분석하고 정확한 견적을 제공해드립니다</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
                    <a href="tel:010-9879-8375" className="flex items-center justify-center gap-2 bg-white text-green-800 px-6 py-3 rounded-full font-bold hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-phone-line"></i>전화 상담
                    </a>
                    <button onClick={() => { const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement; if (btn) btn.click(); setWeedingDetailModalOpen(false); }} className="flex items-center justify-center gap-2 bg-green-700 text-white px-6 py-3 rounded-full font-bold hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-chat-smile-2-line"></i>챗봇 상담
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Annual Subscription Detail Modal ── */}
      {annualDetailModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }} onClick={() => setAnnualDetailModalOpen(false)}>
          <div className="relative max-w-2xl w-full bg-[#fcfaf7] rounded-3xl shadow-2xl overflow-hidden border border-stone-200 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Close */}
            <button onClick={() => setAnnualDetailModalOpen(false)} className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/90 hover:bg-white shadow-lg transition-colors cursor-pointer">
              <i className="ri-close-line text-stone-700 text-xl"></i>
            </button>

            {/* Compact Header */}
            <div className="bg-white/95 border-b border-stone-100 py-4 px-6 text-center sticky top-0 z-10">
              <div className="text-base font-black tracking-tight text-stone-800 flex items-center justify-center gap-1">
                <span className="text-lg">🌿</span> 홈앤그레이브 연간 구독 케어
              </div>
            </div>

            <div className="p-6">
              {/* Hero */}
              <div className="py-4 text-center">
                <h1 className="text-lg md:text-xl font-black text-stone-900 mb-1 leading-tight">
                  전원주택 가치, <span className="text-green-800">연간 구독</span>으로 지키십시오.
                </h1>
                <p className="text-sm text-stone-500 mt-1">정기 관리로 긴급 수리비를 줄이고 자산 가치를 지키세요.</p>
              </div>

              {/* Chart */}
              <div className="bg-white p-5 rounded-2xl border border-stone-100 mb-5">
                <h2 className="text-sm font-bold text-stone-800 mb-3 flex items-center gap-1">
                  <span>📉</span> 구독 시 예상 비용 절감 및 가치 상승
                </h2>
                <div className="relative w-full" style={{height: '240px'}}>
                  <canvas ref={chartRef}></canvas>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-stone-50 p-2 rounded-lg text-stone-600 italic text-center">"긴급 수리비 40% 절감"</div>
                  <div className="bg-stone-50 p-2 rounded-lg text-stone-600 italic text-center">"자산 가치 상시 최상"</div>
                </div>
              </div>

              {/* Membership Tiers */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                {/* Standard */}
                <div className="bg-white p-4 rounded-2xl border border-stone-200 shadow-sm flex flex-col justify-between">
                  <div>
                    <h3 className="text-sm font-black text-stone-800 mb-1">스탠다드</h3>
                    <div className="text-lg font-black text-stone-900 mb-3">연 120<span className="text-xs font-normal text-stone-400 ml-0.5">만~</span></div>
                    <ul className="text-xs text-stone-500 space-y-1.5 leading-tight">
                      <li>• 분기별 정기 방문</li>
                      <li>• 전지 및 예초 작업</li>
                      <li>• 주택 노후 상태 점검</li>
                      <li>• 포토 리포트 전송</li>
                    </ul>
                  </div>
                  <a href="tel:010-9879-8375" className="mt-4 block text-center py-2 bg-stone-200 rounded-lg font-bold text-xs text-stone-700 hover:bg-stone-300 transition-colors cursor-pointer whitespace-nowrap">문의하기</a>
                </div>

                {/* Premium */}
                <div className="bg-green-50 p-4 rounded-2xl border border-green-200 shadow-sm flex flex-col justify-between relative overflow-hidden">
                  <div className="absolute top-0 right-0 bg-green-700 text-white px-2 py-0.5 text-xs font-black uppercase rounded-bl-lg">Premium</div>
                  <div>
                    <h3 className="text-sm font-black text-green-900 mb-1">프리미엄</h3>
                    <div className="text-lg font-black text-green-800 mb-3">연 240<span className="text-xs font-normal text-stone-400 ml-0.5">만~</span></div>
                    <ul className="text-xs text-stone-700 space-y-1.5 leading-tight font-medium">
                      <li>• 격월 정밀 관리 (6회)</li>
                      <li>• 병해충 방제 포함</li>
                      <li>• 시설물 소수리 포함</li>
                      <li>• 긴급 출동 서비스</li>
                      <li>• 드론 촬영 리포트</li>
                    </ul>
                  </div>
                  <a href="tel:010-9879-8375" className="mt-4 block text-center py-2 bg-green-700 rounded-lg font-bold text-xs text-white hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap">문의하기</a>
                </div>
              </div>

              {/* Professional Equipment */}
              <div className="bg-white p-5 rounded-2xl border border-stone-100 mb-5">
                <h3 className="text-sm font-bold text-stone-800 mb-3 text-center">전문 장비 운용</h3>
                <div className="grid grid-cols-4 gap-3 text-center">
                  {[{icon:'ri-scissors-cut-line',label:'전문 예초기'},{icon:'ri-tools-line',label:'정밀 전지기'},{icon:'ri-water-flash-line',label:'고압 세척기'},{icon:'ri-drone-line',label:'드론 촬영'}].map((eq, i) => (
                    <div key={i} className="flex flex-col items-center gap-1">
                      <div className="w-10 h-10 flex items-center justify-center bg-stone-100 rounded-xl">
                        <i className={`${eq.icon} text-stone-600 text-lg`}></i>
                      </div>
                      <span className="text-xs text-stone-500">{eq.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="bg-gradient-to-br from-green-800 to-green-900 p-6 rounded-2xl text-center text-white">
                <p className="font-black text-base mb-1">지금 바로 구독 상담 받으세요</p>
                <p className="text-green-100 text-xs mb-4">전문가가 현장을 분석하고 최적 플랜을 제안해드립니다</p>
                <div className="flex gap-3 justify-center">
                  <a href="tel:010-9879-8375" className="flex items-center gap-1.5 bg-white text-green-800 px-5 py-2.5 rounded-full font-bold text-sm hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-phone-line"></i>전화 상담
                  </a>
                  <button onClick={() => { const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement; if (btn) btn.click(); setAnnualDetailModalOpen(false); }} className="flex items-center gap-1.5 bg-green-700 text-white px-5 py-2.5 rounded-full font-bold text-sm hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-chat-smile-2-line"></i>챗봇 상담
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
